import { ChangeDetectorRef, Component, HostBinding, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { isNullOrUndefined, isNumber } from '../../../../models/utils';
import { DragDropData } from '../../../../models/drag-drop-data';
import { Endpoint, HelpIndex, MessageType, SettingsType } from '../../../../models/enums';
import { FilterTemplateType, GenericColDef, GridContext, SortByFilter } from '../../../../models/grid-context';
import { ActionType, ObjectActionListType, Permissions } from '../../../../models/permission';
import { SettingsNavigationContentComponent } from '../../../../models/settings-navigation-content-component';
import { Template, TemplateType } from '../../../../models/template';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { LeftRightSelectionBoxComponent } from '../../../left-right-selection-box/left-right-selection-box.component';
import * as _ from 'lodash';
import { MetadataService } from '../../../../../app/services/metadata/metadata.service';
import { DragDropCellComponent } from '../../../grid-templates/drag-drop-cell/drag-drop-cell.component';
import { DragCellComponent } from '../../../grid-templates/drag-cell/drag-cell.component';

@Component({
    standalone: false,
    selector: 'res-metadata-fields',
    templateUrl: './metadata-fields.component.html',
    styleUrls: ['./metadata-fields.component.scss']
})
export class MetadataFieldsComponent implements SettingsNavigationContentComponent {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild('contentForm') contentForm: NgForm;
    @ViewChild(LeftRightSelectionBoxComponent, { static: true }) leftRightSelectionBoxComponent: LeftRightSelectionBoxComponent;

    isBusy = false;
    isBusyMessage: string;
    validChanges = true;
    helpIndex: HelpIndex;
    handle: string;
    automationId: string;
    type: TemplateType;
    itemsLeft: Array<DragDropData>;
    itemsRight: Array<DragDropData>;
    initialItemLeftList: Array<DragDropData>;
    initialItemRightList: Array<DragDropData>;
    originalFieldsMap: Map<string, string>; // name, removed (true,false)
    currentFieldsMap: Map<string, boolean>; // name, second value doesn't matter so always set to true
    currentDroppedFieldsMap: Map<string, boolean>;

    rightListTitle: string;

    filterQuery = '';
    filterViewFieldsQuery = '';
    useDragAndDrop = false;
    showLeftRightButton = true;

    // permissions
    permissionType: ObjectActionListType;
    hasCreatePermissions: boolean;

    leftListColumns: Array<GenericColDef>;
    rightListColumns: Array<GenericColDef>;
    sortItemsRight: boolean;
    showRightHeaderContextMenu: boolean;
    adjustHeaderCheckboxStyles: boolean;

    leftListGridContext = new GridContext({ sortBy: 'name', sortAscending: true, sortEnabled: false, inlineGridFilter: true, onFilterApply: (queryStr: string) => this.onFilterApply(queryStr) });
    rightListGridContext: any;
    isSystemUser = false;

    get isDirty(): boolean {
        if (this.contentForm) {
            return this.contentForm.dirty;
        }
        return false;
    }
    set isDirty(value: boolean) {
        if (this.contentForm && this.contentForm.control) {
            if (value) {
                this.contentForm.control.markAsDirty();
            } else {
                this.contentForm.control.markAsPristine();
            }
        }
    }

    constructor(private commonService: CommonService, private restService: RestService, private metadataService: MetadataService, public changeDetector: ChangeDetectorRef) { }

    selectedTreeItemChanged(node: GenericTreeNode, projectHandle?: string, settingsType?: SettingsType): void {
        const reloadColums = this.handle ? true : false;
        const previousType = this.type;
        if (node.data instanceof Template) {
            this.type = node.data.templateType;
            this.handle = node.data.handle;
        } else {
            this.type = node.name === 'Metadata Search Fields' ? TemplateType.SEARCH_FIELDS : TemplateType.DOCUMENT_METADATA;
            this.handle = projectHandle ? projectHandle : this.commonService.currentOrganizationHandle;
        }
        this.useDragAndDrop = this.type === TemplateType.SEARCH_FIELDS ? false : true;
        this.showLeftRightButton = this.type === TemplateType.SEARCH_FIELDS ? true : false;
        if (this.type === TemplateType.SEARCH_FIELDS) {
            this.helpIndex = projectHandle ? HelpIndex.myedisc_case_search_fields_dlg : (settingsType === SettingsType.system ? HelpIndex.sys_template_metadata_search_fields_dlg : HelpIndex.org_search_fields_template_dlg);
            this.automationId = 'metadata-search-fields';
            const automationIdLeft = this.automationId + '-available-fields';
            const automationIdRight = this.automationId + '-selected-fields';
            this.rightListTitle = 'Metadata Search Fields';
            this.sortItemsRight = true;
            this.showRightHeaderContextMenu = true;
            this.adjustHeaderCheckboxStyles = false;
            this.leftListColumns = new Array<GenericColDef>(
                {
                    colId: 'checkbox', automationId: automationIdLeft,
                    checkboxSelection: true, headerCheckboxSelection: true,
                    minWidth: 36, maxWidth: 36, width: 36,
                    suppressSizeToFit: true, suppressMovable: true
                },
                {
                    headerName: 'Field Name', field: 'name', sortBy: 'name', colId: 'name', automationId: automationIdLeft, filterType: FilterTemplateType.freeform, suppressMovable: true
                }
            );
            this.rightListColumns = new Array<GenericColDef>(
                {
                    colId: 'checkbox', automationId: automationIdRight,
                    checkboxSelection: true, headerCheckboxSelection: true,
                    minWidth: 36, maxWidth: 36, width: 36,
                    suppressSizeToFit: true, suppressMovable: true
                },
                {
                    headerName: 'Field Name', field: 'name', sortBy: 'name', automationId: automationIdRight
                }
            );
            this.rightListGridContext = new GridContext({ sortBy: 'name', sortAscending: true, sortEnabled: false });
            this.isSystemUser = (settingsType === SettingsType.system && (this.commonService.currentUser?.originalCustomerName === 'super_system_customer' || this.commonService.currentUser.importedFromSystemUser));
            this.permissionType = projectHandle ? ObjectActionListType.PRJ_METADATA_SEARCH_FIELDS : this.isSystemUser ? ObjectActionListType.SYSTEM_METADATA_SEARCH_FIELDS : ObjectActionListType.ORG_METADATA_SEARCH_FIELDS;
        } else {
            this.helpIndex = projectHandle ? HelpIndex.myedisc_case_metadata_view_config_dlg : (settingsType === SettingsType.system ? HelpIndex.sys_template_metadata_view_fields_dlg : HelpIndex.org_metadata_template_dlg);
            this.automationId = 'metadata-view-fields';
            const automationIdLeft = this.automationId + '-available-fields';
            const automationIdRight = this.automationId + '-selected-fields';
            this.rightListTitle = 'Metadata View Fields';
            this.sortItemsRight = false;
            this.showRightHeaderContextMenu = false;
            this.adjustHeaderCheckboxStyles = true;
            this.leftListColumns = new Array<GenericColDef>({
                colId: 'checkbox',
                automationId: automationIdLeft,
                minWidth: 50, maxWidth: 50, width: 50,
                headerCheckboxSelection: true,
                cellRenderer: DragCellComponent,
                colSpan: () => 2,
                field: 'name',
                headerName: '',
                cellRendererParams: {
                    inlineEdit: () => false,
                    required: () => false,
                    checkable: true,
                    dragScope: ['availableField']
                }
            }, {
                headerName: 'Field Name',
                field: 'name',
                automationId: automationIdLeft,
                lockVisible: true,
                filterType: FilterTemplateType.freeform,
                defaultSortAscending: true,
                defaultSortBy: 'name'
            });
            this.rightListColumns = new Array<GenericColDef>(
                {
                    colId: 'checkbox',
                    automationId: automationIdRight,
                    minWidth: 50, maxWidth: 50, width: 50,
                    headerCheckboxSelection: true,
                    cellRenderer: DragDropCellComponent,
                    colSpan: () => 2,
                    field: 'name',
                    headerName: '',
                    cellRendererParams: {
                        showDragDots: true,
                        showCheckboxes: true,
                        isRowDeletable: true,
                        isFiltering: false,
                        dragScope: (item: DragDropData) => ['field'],
                        dropScope: (item: DragDropData) => ['field', 'availableField'],
                        onDrop: (dragData: DragDropData, dropData: DragDropData) => this.leftRightSelectionBoxComponent.onDropRight(dragData, dropData, this.filterQuery),
                        deleteRow: (deletedRow: any) => this.leftRightSelectionBoxComponent.onDropLeft(deletedRow, null, true, this.filterQuery)
                    }
                },
                {
                    colId: 'field-name',
                    headerName: 'Field Name',
                    field: 'name',
                    automationId: automationIdRight,
                    filterType: FilterTemplateType.freeform,
                });
            this.rightListGridContext = new GridContext({ sortAscending: false, sortEnabled: false, inlineGridFilter: true, onFilterApply: (queryStr: string, type?: string, filterColumn?: string, isResetAllFilter?: boolean, applyFilterOnChildren?: boolean, isClearFilter?: boolean) => this.onFilterViewFieldsApply(queryStr, null, isClearFilter) });
            this.isSystemUser = (settingsType === SettingsType.system && (this.commonService.currentUser?.originalCustomerName === 'super_system_customer' || this.commonService.currentUser.importedFromSystemUser));
            this.permissionType = projectHandle ? ObjectActionListType.PRJ_METADATA_VIEW_FIELDS : this.isSystemUser ? ObjectActionListType.SYSTEM_METADATA_VIEW_FIELDS : ObjectActionListType.ORG_METADATA_VIEW_FIELDS;
        }

        this.hasCreatePermissions = Permissions.checkUserPermission(this.permissionType, ActionType.CREATE);

        if (this.leftListColumns.find(c => c.colId === 'checkbox')) {
            if (!this.hasCreatePermissions) {
                this.leftListColumns = this.leftListColumns.filter(col => col.colId !== 'checkbox');
            }
        }

        if (this.rightListColumns.find(c => c.colId === 'checkbox')) {
            if (!this.hasCreatePermissions) {
                this.rightListColumns = this.rightListColumns.filter(col => col.colId !== 'checkbox');
            }
        }
        if (reloadColums) {
            if (previousType && previousType !== this.type && this.type === TemplateType.DOCUMENT_METADATA) {
                this.leftRightSelectionBoxComponent.genericGrids.first.gridOptions.defaultColDef.cellRenderer = undefined;
                this.leftRightSelectionBoxComponent.genericGrids.last.gridOptions.defaultColDef.cellRenderer = undefined;
                this.rightListGridContext.sortBy = undefined;
            }

            if (this.type === TemplateType.DOCUMENT_METADATA) {
                this.rightListGridContext.onFilterApply('', FilterTemplateType.clear, this.rightListColumns[1].headerName);
            }
            this.leftRightSelectionBoxComponent.genericGrids.first.setColumnDefs(this.leftListColumns);
            this.leftRightSelectionBoxComponent.genericGrids.last.setColumnDefs(this.rightListColumns);
        }
        this.loadSettingsData();

        if (this.leftRightSelectionBoxComponent && this.leftRightSelectionBoxComponent.genericGrids && this.leftRightSelectionBoxComponent.genericGrids.first) {
            this.leftRightSelectionBoxComponent.genericGrids.first.isDataFiltered = false;
            this.leftRightSelectionBoxComponent.genericGrids.first.gridContext.clearAllFilters.emit();
            this.leftRightSelectionBoxComponent.genericGrids.first.gridContext.sortBy = 'name';
            this.leftRightSelectionBoxComponent.genericGrids.first.gridApi.deselectAll();
            this.leftRightSelectionBoxComponent.genericGrids.first.headerContextMenuOptions = [];
            this.leftRightSelectionBoxComponent.genericGrids.first.gridOptions.onGridReady(null);
        }
    }

    areItemsDirty(items, mode): any {
        this.isDirty = false;
        if (items && (items.length > 0)) {
            items.forEach((entry, index) => {
                if (this.originalFieldsMap.has(entry.name)) {
                    const removed = this.originalFieldsMap.get(entry.name);
                    if (!isNullOrUndefined(removed)) {
                        if ((removed === 'false') && (mode === 'remove')) {
                            this.originalFieldsMap.set(entry.name, 'true');
                        } else if ((removed === 'true') && (mode === 'add')) {
                            this.originalFieldsMap.set(entry.name, 'false');
                        }
                    }
                }
            });
        }

        const mapValues = Array.from(this.originalFieldsMap.values());
        const anyItemsRemoved = mapValues.join(',').includes('true');
        if (anyItemsRemoved) {
            this.isDirty = true;
        } else if (this.currentFieldsMap.size > 0) {
            this.isDirty = true;
        }
    }

    loadSettingsData(): void {
        if (this.handle) {
            if (this.type === TemplateType.SEARCH_FIELDS) {
                this.leftRightSelectionBoxComponent.rightListRequired = false;
                this.loadSearchFields();
            } else {
                this.leftRightSelectionBoxComponent.rightListRequired = true;
                this.leftRightSelectionBoxComponent.rightListRequiredTooltip = 'At least one field is required';
                this.loadViewFields();
            }
        }
    }

    loadSearchFields(): void {
        this.isBusyMessage = 'Loading Metadata Search Fields...';
        this.isBusy = true;
        this.originalFieldsMap = new Map<string, string>();
        this.currentFieldsMap = new Map<string, boolean>();
        this.restService.post(Endpoint.projectManager_projectGetSearchMetadataProfile, { profileHandle: this.handle, requestHandle: this.handle, systemScope: this.isSystemUser }).then(responseProfile => {
            this.itemsRight = new Array<DragDropData>();
            if (responseProfile && responseProfile.requestHandle === this.handle && responseProfile.profile && responseProfile.profile.metadataFields) {
                responseProfile.profile.metadataFields.forEach((element, index) => {
                    this.itemsRight.push(new DragDropData({
                        name: element,
                        isDisabled: element === 'contents'
                    }));
                    // list to removed fields
                    this.originalFieldsMap.set(element, 'false');
                });
            }
            this.sortList(this.itemsRight);
            this.leftRightSelectionBoxComponent.setRightItems(this.itemsRight);
            this.initialItemRightList = this.itemsRight;
            this.restService.post(Endpoint.projectManager_getAllSearchMetadataFields, { requestHandle: this.handle, systemScope: this.isSystemUser }).then(responseAllFields => {
                this.itemsLeft = new Array<DragDropData>();
                if (responseAllFields && responseAllFields.requestHandle === this.handle && responseAllFields.fieldNames) {
                    responseAllFields.fieldNames.forEach((element, index) => {
                        const rightItem = this.itemsRight.filter(item => item.name === element).pop();
                        if (!rightItem) {
                            this.itemsLeft.push(new DragDropData({
                                name: element
                            }));
                        }
                    });
                }
                this.sortList(this.itemsLeft);
                this.leftRightSelectionBoxComponent.setLeftItems(this.itemsLeft);
                this.initialItemLeftList = this.leftRightSelectionBoxComponent.initialItemLeftList = this.leftRightSelectionBoxComponent.itemsLeft;
                if (this.filterQuery !== '') {
                    this.onFilterApply(this.filterQuery);
                }
                this.isBusy = false;
                this.isDirty = false;
            }, error => {
                this.isBusy = false;
            });
        }, error => {
            this.isBusy = false;
        });
    }

    loadViewFields(): void {
        this.isBusyMessage = 'Loading Metadata View Fields...';
        this.isBusy = true;
        this.originalFieldsMap = new Map<string, string>();
        this.currentFieldsMap = new Map<string, boolean>();
        this.currentDroppedFieldsMap = new Map<string, boolean>();
        this.metadataService.fieldDescriptions().then(responseDescriptions => {
            const descriptions: Array<any> = responseDescriptions ? responseDescriptions : [];
            this.restService.post(Endpoint.projectManager_getDocumentMetadataTemplate, { handle: this.handle, systemScope: this.isSystemUser }).then(responseProfile => {
                this.itemsRight = new Array<DragDropData>();
                if (responseProfile && responseProfile.metadataFolders) {
                    responseProfile.metadataFolders.forEach((folder, index) => {
                        if (folder.handle && folder.fields) {
                            folder.fields.forEach(element => {
                                const desc = descriptions.filter(d => d.name === element).pop();
                                // list we will edit
                                this.itemsRight.push(new DragDropData({
                                    name: element,
                                    tooltip: desc ? desc.value : '',
                                    additionalData: [desc ? desc.value : null, folder.handle]
                                }));
                                this.originalFieldsMap.set(element, 'false');
                            });
                        }
                    });
                }
                this.leftRightSelectionBoxComponent.setRightItems(this.itemsRight);
                this.initialItemRightList = this.itemsRight;
                this.restService.post(Endpoint.realmManager_getAllFieldsDocumentMetadataTemplate, { containerId: this.handle, systemScope: this.isSystemUser }).then(responseAllFields => {
                    this.itemsLeft = new Array<DragDropData>();
                    if (responseAllFields && responseAllFields.metadataFolders) {
                        responseAllFields.metadataFolders.forEach((folder, index) => {
                            if (folder.handle && folder.fields) {
                                folder.fields.forEach(element => {
                                    const rightItem = this.itemsRight.filter(item => item.name === element).pop();
                                    if (!rightItem) {
                                        const desc = descriptions.filter(d => d.name === element).pop();
                                        // list we will edit
                                        this.itemsLeft.push(new DragDropData({
                                            name: element,
                                            tooltip: desc ? desc.value : '',
                                            additionalData: [desc ? desc.value : null, folder.handle]
                                        }));
                                    }
                                });
                            }
                        });
                    }
                    this.sortList(this.itemsLeft);
                    this.leftRightSelectionBoxComponent.setLeftItems(this.itemsLeft);
                    this.initialItemLeftList = this.leftRightSelectionBoxComponent.initialItemLeftList = this.leftRightSelectionBoxComponent.itemsLeft;
                    if (this.filterQuery !== '') {
                        this.onFilterApply(this.filterQuery);
                    }
                    if (this.filterViewFieldsQuery !== '') {
                        this.onFilterViewFieldsApply(this.filterViewFieldsQuery);
                    }
                    this.isBusy = false;
                    this.isDirty = false;
                }, error => {
                    this.isBusy = false;
                });
            }, error => {
                this.isBusy = false;
            });
        }, error => {
            this.isBusy = false;
        });
    }

    saveChanges(fromAutoSave = false): Promise<boolean> {
        if (this.type === TemplateType.SEARCH_FIELDS) {
            return this.saveSearchFields(fromAutoSave);
        } else {
            return this.saveViewFields(fromAutoSave);
        }
    }

    saveSearchFields(fromAutoSave = false): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if (this.handle) {
                this.isBusyMessage = 'Saving Metadata Search Fields...';
                this.isBusy = true;
                const metadataFields = this.itemsRight.map(item => item.name);
                this.restService.post(Endpoint.projectManager_projectSetSearchMetadataProfile, { profileHandle: this.handle, metadataFields: metadataFields, systemScope: this.isSystemUser }).then(response => {
                    this.isDirty = false;
                    this.isBusy = false;
                    if (!fromAutoSave) {
                        this.loadSettingsData();
                    }
                    resolve(true);
                }, error => {
                    this.isBusy = false;
                    resolve(false);
                });
            } else {
                resolve(false);
            }
        });
    }

    saveViewFields(fromAutoSave = false): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if (this.handle) {
                this.isBusyMessage = 'Saving Metadata View Fields...';
                this.isBusy = true;
                const items = this.filterViewFieldsQuery ? this.initialItemRightList : this.leftRightSelectionBoxComponent.itemsRight;
                const folderNames = items.map(item => item.additionalData[1]).filter((x, i, a) => a.indexOf(x) === i);
                const metadataFolders = new Array<any>();
                // ECASL - ECASL-35529
                // the reason behind combining all of the categories fields into 'Digital_Reef_Properties' is because the request is putting the fields in different categories
                // and each category is iterated over in the backend and their values are generated in the XML which is messing up the order on 'Save' if the fields happen to be in different categories. 
                // All of this can be done in the backend to handle it but there is no need to do all of that stuff as categories dont have a meaning in the UI anymore in 5.x
                // Commented on 6/16/2021 - Sravya
                this.itemsRight.forEach(item => {
                    item.additionalData[1] = 'Digital_Reef_Properties';
                });
                if (folderNames.indexOf('Digital_Reef_Properties') === -1) {
                    folderNames.push('Digital_Reef_Properties');
                }
                folderNames.forEach(folder => {
                    metadataFolders.push({
                        attributes: [],
                        subFolders: [],
                        handle: folder,
                        fields: items.filter(f => f.additionalData[1] === folder).map(item => item.name)
                    });
                });
                this.restService.post(Endpoint.projectManager_setDocumentMetadataTemplate,
                    {
                        handle: this.handle,
                        projectHandle: this.handle,
                        documentMetadataTemplate: '<xml></xml>',
                        metadataFolders: metadataFolders,
                        systemScope: this.isSystemUser
                    }).then(() => {
                        this.isDirty = false;
                        this.isBusy = false;
                        if (!fromAutoSave) {
                            this.loadSettingsData();
                        }
                        resolve(true);
                    }, () => {
                        this.isBusy = false;
                        resolve(false);
                    });
            } else {
                resolve(false);
            }
        });
    }

    discardChanges(): void {
        this.isDirty = false;
        this.loadSettingsData();
    }

    itemsChanged(items): void {
        if (items && items.items && (items.items.length > 0)) {
            this.leftRightSelectionBoxComponent.genericGrids.first.gridApi.deselectAll();
            this.leftRightSelectionBoxComponent.moveActiveItems(items.items);
            this.dirty(items);
            items.items.forEach(item => {
                if (items.mode === 'add') {
                    this.initialItemLeftList.splice(this.initialItemLeftList.indexOf(item), 1);
                } else {
                    if (!this.initialItemLeftList.includes(item)) {
                        this.initialItemLeftList.push(item);
                    }
                }
            });

            this.leftRightSelectionBoxComponent.initialItemLeftList = this.initialItemLeftList;
            this.initialItemRightList = this.leftRightSelectionBoxComponent.itemsRight.map(obj => Object.assign({}, obj));

            if (this.filterQuery !== '') {
                this.onFilterApply(this.filterQuery);
            }
            if (this.filterViewFieldsQuery !== '') {
                this.onFilterViewFieldsApply(this.filterViewFieldsQuery);
            }
        }
    }

    afterDropUpdate(dropped): void {
        // update currentFieldsMap
        if (!isNullOrUndefined(dropped.items)) {
            dropped.items.forEach((entry) => {
                const currentItem = this.currentFieldsMap.get(entry.name);
                if (dropped.type === 'left' && !isNullOrUndefined(currentItem)) {
                    if (this.filterViewFieldsQuery) {
                        this.currentDroppedFieldsMap.delete(entry.name);
                    }
                    this.currentFieldsMap.delete(entry.name);
                } else if (dropped.type === 'right' && isNullOrUndefined(currentItem)) {
                    if (this.filterViewFieldsQuery) {
                        this.currentDroppedFieldsMap.set(entry.name, true);
                    }
                    this.currentFieldsMap.set(entry.name, true); // the value doesn't matter
                }

                // Convert initialItemRightList, currentDroppedFieldsMap to a Set for quick lookups
                let initialSet = new Set(this.initialItemRightList.map(item => item.name));
                const currentSet = new Set(this.currentDroppedFieldsMap.keys());

                // Remove items from initialItemRightList if they are not in currentDroppedFieldsMap
                if (dropped.type === 'left' && entry.name) {
                    this.initialItemRightList = this.initialItemRightList.filter(item => item.name !== entry.name)
                    // Update initialSet to reflect the removal
                    initialSet = new Set(this.initialItemRightList.map(item => item.name));
                } else {
                    // Add missing items from currentDroppedFieldsMap to initialItemRightList
                    currentSet.forEach(name => {
                        if (!initialSet.has(name) && entry?.name === name) {
                            this.initialItemRightList.push({
                                name: entry.name,
                                additionalData: entry.additionalData,
                                isDisabled: entry.isDisabled,
                                isEmptyDropRow: entry.isEmptyDropRow,
                                externalName: entry.externalName,
                                tooltip: entry.tooltip
                            });
                            // Update initialSet to include the newly added item
                            initialSet.add(entry.name);
                        }
                    });
                }
            });
            this.leftRightSelectionBoxComponent.genericGrids.last.isBusyMessage = "Loading ....";
            this.leftRightSelectionBoxComponent.genericGrids.last.isBusy = true;
            setTimeout(() => {
                if (this.filterViewFieldsQuery && dropped.type === 'left') {
                    this.itemsRight = this.initialItemRightList;
                    this.onFilterViewFieldsApply(this.filterViewFieldsQuery, true);
                } else if (this.filterViewFieldsQuery && dropped.type === 'right') {
                    this.itemsRight = this.initialItemRightList;
                    this.onFilterViewFieldsApply(this.filterViewFieldsQuery, true);
                }
                this.changeDetector.detectChanges();

                this.areItemsDirty(dropped.items, dropped.type === 'left' ? 'remove' : 'add');
                this.onFilterApply(this.filterQuery);
                this.leftRightSelectionBoxComponent.genericGrids.last.isBusy = false;
            }, 100);
        }
    }

    onFilterApply(queryStr: string): void {
        this.filterQuery = queryStr;
        const filtered = _.filter(this.initialItemLeftList, (field) => field.name ? field.name.toLowerCase().indexOf(queryStr.toLowerCase()) !== -1 : false);
        this.leftRightSelectionBoxComponent.setLeftItems(filtered);
    }
    onFilterViewFieldsApply(queryStr: string, showDroppedItems?: boolean, isClearFilter?: boolean): void {
        this.setDndDropzone(queryStr, showDroppedItems, isClearFilter);
        this.filterViewFieldsQuery = queryStr;
    }

    setDndDropzone(isFiltering: string, showDroppedItems?: boolean, isClearFilter?: boolean): void {
        const checkBoxCol = this.rightListColumns.find(col => col.colId === 'checkbox');
        if (checkBoxCol) {
            checkBoxCol.cellRendererParams.isFiltering = Boolean(isFiltering);
        }
        this.leftRightSelectionBoxComponent?.genericGrids?.last?.setColumnDefs(this.rightListColumns);
        this.commonService.sendMessage(MessageType.filterGridHeadersChanged, { colDef: this.rightListColumns[1], filterValue: isFiltering, isClearFilter });
        this.filterViewFields(isFiltering, showDroppedItems);
    }

    filterViewFields(queryStr: string, showDroppedItems?: boolean): void {
        this.leftRightSelectionBoxComponent.genericGrids.last.isBusyMessage = "Loading ....";
        this.leftRightSelectionBoxComponent.genericGrids.last.isBusy = true;
        if (!this.filterViewFieldsQuery) {
            this.initialItemRightList = this.leftRightSelectionBoxComponent.itemsRight;
        }
        const filtered = _.filter(this.initialItemRightList, (field) => field.name ? field.name.toLowerCase().indexOf(queryStr.toLowerCase()) !== -1 : false);
        if (showDroppedItems) {
            const droppedItems = new Set(this.currentDroppedFieldsMap.keys());
            droppedItems.forEach(entry => {
                const item = _.find(this.initialItemRightList, attr => attr.name === entry);
                if (item && !filtered.some(filteredItem => filteredItem.name === item.name)) {
                    filtered.push(item);
                }
            });
        }
        this.leftRightSelectionBoxComponent.setRightItems(filtered);
        this.leftRightSelectionBoxComponent.genericGrids.last.isBusy = false;
    }

    dirty(items): void {
        this.isDirty = false;
        if (items && items.items && (items.items.length > 0)) {
            items.items.forEach((entry, index) => {
                if (this.originalFieldsMap.has(entry.name)) {
                    const removed = this.originalFieldsMap.get(entry.name);
                    if (!isNullOrUndefined(removed)) {
                        if ((removed === 'false') && (items.mode === 'remove')) {
                            this.originalFieldsMap.set(entry.name, 'true');
                        } else if ((removed === 'true') && (items.mode === 'add')) {
                            this.originalFieldsMap.set(entry.name, 'false');
                        }
                    }
                } else { // not an original items so update currentMetadataSearchFieldMap
                    const currentItem = this.currentFieldsMap.get(entry.name);
                    if ((items.mode === 'remove') && !isNullOrUndefined(currentItem)) {
                        this.currentFieldsMap.delete(entry.name);
                    } else if ((items.mode === 'add') && isNullOrUndefined(currentItem)) {
                        this.currentFieldsMap.set(entry.name, true); // the value doesn't matter
                    }
                }
            });
        }

        const mapValues = Array.from(this.originalFieldsMap.values());
        const anyItemsRemoved = mapValues.join(',').includes('true');
        if (anyItemsRemoved) {
            this.isDirty = true;
        } else if (this.currentFieldsMap.size > 0) {
            this.isDirty = true;
        }
    }

    sortList(list: Array<DragDropData>): void {
        if (list) {
            list.sort((a, b) => {
                const nameA = a.name.toLowerCase();
                const nameB = b.name.toLowerCase();
                if (nameA < nameB) {
                    return -1;
                }
                if (nameA > nameB) {
                    return 1;
                }
                return 0;
            });
        }
    }

    openHelp = () => this.commonService.openHelp(this.helpIndex);

    isSaveDisabled(): boolean {
        let isDisabled = true;
        if (!this.isBusy && this.isDirty && this.itemsRight && (this.itemsRight.length > 0)) {
            isDisabled = false;
        }
        return isDisabled;
    }

    isDiscardDisabled(): boolean {
        let isDisabled = true;
        if (!this.isBusy && this.isDirty) {
            isDisabled = false;
        }
        return isDisabled;
    }
}
